<template>
  <div class="colleges">
    <header class="head">
      <div>
        <h1>Colleges</h1>
        <p class="muted">
          Manage the colleges available to students. Approve users for their college;
          access has no calendar restriction. Polls stay separate by college.
        </p>
      </div>
    </header>

    <form class="create card-ish" @submit.prevent="create">
      <h2>Add college</h2>
      <div class="row">
        <label>
          College name
          <input v-model="form.name" type="text" required placeholder="e.g. Stanford University" />
        </label>
        <label class="check">
          <input v-model="form.enabled" type="checkbox" />
          Enabled
        </label>
        <button class="btn btn-primary" type="submit" :disabled="saving">
          {{ saving ? 'Adding…' : 'Add' }}
        </button>
      </div>
      <p v-if="formError" class="error">{{ formError }}</p>
    </form>

    <section class="requests">
      <h2>Students asked us to add these</h2>
      <ul v-if="requests.length">
        <li v-for="r in requests" :key="r.id">
          <strong>{{ r.collegeName }}</strong>
          <span class="muted"> — {{ r.name || r.email }}</span>
        </li>
      </ul>
      <p v-else class="muted">No student college requests right now.</p>
    </section>

    <p v-if="loading" class="muted">Loading…</p>
    <p v-else-if="error" class="error">{{ error }}</p>
    <table v-else class="table">
      <thead>
        <tr>
          <th>College</th>
          <th>Status</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="c in colleges" :key="c.id">
          <td>
            <input
              v-if="editing === c.id"
              v-model="editForm.name"
              type="text"
              class="inline-input"
            />
            <span v-else>{{ c.name }}</span>
          </td>
          <td>
            <span class="status" :data-on="c.enabled ? '1' : '0'">
              {{ c.enabled ? 'Enabled' : 'Disabled' }}
            </span>
          </td>
          <td class="row-actions">
            <template v-if="editing === c.id">
              <button class="btn btn-primary" type="button" @click="saveEdit(c.id)">Save</button>
              <button class="btn btn-secondary" type="button" @click="cancelEdit">Cancel</button>
            </template>
            <template v-else>
              <button class="btn btn-secondary" type="button" @click="startEdit(c)">Edit</button>
              <button class="btn btn-secondary" type="button" @click="toggle(c)">
                {{ c.enabled ? 'Disable' : 'Enable' }}
              </button>
              <button class="btn btn-danger" type="button" @click="remove(c)">Remove</button>
            </template>
          </td>
        </tr>
        <tr v-if="!colleges.length">
          <td colspan="3" class="muted">No colleges yet — add one above.</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import {
  adminListColleges,
  adminListCollegeRequests,
  adminCreateCollege,
  adminUpdateCollege,
  adminDeleteCollege,
} from '../services/api'

const colleges = ref([])
const requests = ref([])
const loading = ref(true)
const saving = ref(false)
const error = ref('')
const formError = ref('')
const editing = ref(null)
const form = ref({ name: '', enabled: true })
const editForm = ref({ name: '' })

async function load() {
  loading.value = true
  error.value = ''
  try {
    const [res, reqs] = await Promise.all([
      adminListColleges(),
      adminListCollegeRequests().catch(() => ({ requests: [] })),
    ])
    colleges.value = res.colleges || []
    requests.value = reqs.requests || []
  } catch (e) {
    error.value = e.message || 'Failed to load colleges'
  } finally {
    loading.value = false
  }
}

async function create() {
  formError.value = ''
  saving.value = true
  try {
    await adminCreateCollege({
      name: form.value.name,
      enabled: form.value.enabled,
    })
    form.value = { name: '', enabled: true }
    await load()
  } catch (e) {
    formError.value = e.message || 'Could not add college'
  } finally {
    saving.value = false
  }
}

function startEdit(c) {
  editing.value = c.id
  editForm.value = { name: c.name }
}

function cancelEdit() {
  editing.value = null
}

async function saveEdit(id) {
  try {
    await adminUpdateCollege(id, {
      name: editForm.value.name,
    })
    editing.value = null
    await load()
  } catch (e) {
    error.value = e.message || 'Update failed'
  }
}

async function toggle(c) {
  try {
    await adminUpdateCollege(c.id, { enabled: !c.enabled })
    await load()
  } catch (e) {
    error.value = e.message || 'Could not update status'
  }
}

async function remove(c) {
  if (!confirm(`Remove ${c.name}? Students will no longer see it.`)) return
  try {
    await adminDeleteCollege(c.id)
    await load()
  } catch (e) {
    error.value = e.message || 'Could not remove college'
  }
}

onMounted(load)
</script>

<style scoped>
.head {
  margin-bottom: 1.5rem;
}

h1 {
  margin: 0 0 0.35rem;
}

h2 {
  margin: 0 0 0.85rem;
  font-size: 1.1rem;
}

.create {
  margin-bottom: 1.75rem;
  padding: 1.15rem 1.25rem;
  background: var(--paper-warm);
  border: 1px solid var(--border);
  border-radius: 8px;
}

.row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

label {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.8rem;
  font-weight: 600;
  flex: 1 1 10rem;
}

label.check {
  flex-direction: row;
  align-items: center;
  gap: 0.45rem;
  flex: 0 0 auto;
  padding-bottom: 0.55rem;
}

input[type='text'],
.inline-input {
  font: inherit;
  font-weight: 400;
  padding: 0.55rem 0.65rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--cream);
}

.table {
  width: 100%;
  border-collapse: collapse;
  background: var(--cream);
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
}

th,
td {
  text-align: left;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border);
  vertical-align: middle;
}

th {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--muted);
  background: var(--paper-warm);
}

.row-actions {
  display: flex;
  gap: 0.4rem;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.status[data-on='1'] {
  color: var(--success);
  font-weight: 600;
}

.status[data-on='0'] {
  color: var(--muted);
  font-weight: 600;
}

.error {
  color: var(--danger);
  margin: 0.75rem 0 0;
}

.requests {
  margin-bottom: 1.75rem;
}

.requests ul {
  margin: 0.5rem 0 0;
  padding-left: 1.15rem;
}
</style>
